import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { generateInviteToken, generateInviteURL } from '../lib/invites';
import { createEventInviteNotification } from '../lib/notifications';
import { sendEventInviteSMS } from '../lib/sms';
import { useAuthState } from '../hooks/useAuthState';
import { getEvent } from '../lib/firestore/events';
import QRCodeModal from './QRCodeModal';
import '../styles/InviteModal.css';

const InviteModal = ({ isOpen, onClose, eventId, eventTitle, userId }) => {
  const { user: currentUser } = useAuthState();
  const [contacts, setContacts] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('contacts');
  const [showQRModal, setShowQRModal] = useState(false);
  const [event, setEvent] = useState(null);

  useEffect(() => {
    if (isOpen && userId) {
      loadContactsAndFriends();
    }
    if (isOpen && eventId) {
      loadEvent();
    }
  }, [isOpen, userId, eventId]);

  const loadEvent = async () => {
    if (!eventId) return;
    try {
      const eventData = await getEvent(eventId);
      setEvent(eventData);
    } catch (error) {
      console.error('Error loading event:', error);
    }
  };

  const loadContactsAndFriends = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      // Load contacts
      const contactsRef = collection(db, 'users', userId, 'contacts');
      const contactsSnapshot = await getDocs(contactsRef);
      const contactsData = await Promise.all(
        contactsSnapshot.docs.map(async (contactDoc) => {
          const contact = { id: contactDoc.id, ...contactDoc.data() };
          // Check if contact is a user
          if (contact.phoneE164) {
            const phoneDigits = contact.phoneE164.replace(/\D/g, '');
            const userQuery = query(
              collection(db, 'users'),
              where('phone', '==', contact.phoneE164)
            );
            const userSnapshot = await getDocs(userQuery);
            if (userSnapshot.empty) {
              const phoneNumberQuery = query(
                collection(db, 'users'),
                where('phoneNumber', '==', phoneDigits)
              );
              const phoneNumberSnapshot = await getDocs(phoneNumberQuery);
              if (!phoneNumberSnapshot.empty) {
                const userDoc = phoneNumberSnapshot.docs[0];
                contact.userId = userDoc.id;
                contact.userData = userDoc.data();
              }
            } else {
              const userDoc = userSnapshot.docs[0];
              contact.userId = userDoc.id;
              contact.userData = userDoc.data();
            }
          }
          return contact;
        })
      );

      // Load friends
      // Query friendships where user is part of the pair
      // Since pairId format is min(uid1,uid2)_max(uid1,uid2), we need to query both possibilities
      const userFriendships = [];
      try {
        // Query where user is the 'a' field (smaller ID)
        const friendshipsQueryA = query(
          collection(db, 'friendships'),
          where('a', '==', userId)
        );
        const snapshotA = await getDocs(friendshipsQueryA);
        snapshotA.forEach((doc) => {
          const data = doc.data();
          if (data.b && data.b !== userId) {
            userFriendships.push(data.b);
          }
        });

        // Query where user is the 'b' field (larger ID)
        const friendshipsQueryB = query(
          collection(db, 'friendships'),
          where('b', '==', userId)
        );
        const snapshotB = await getDocs(friendshipsQueryB);
        snapshotB.forEach((doc) => {
          const data = doc.data();
          if (data.a && data.a !== userId) {
            userFriendships.push(data.a);
          }
        });
      } catch (friendshipsError) {
        console.error('Error loading friendships:', friendshipsError);
        // Continue without friends if query fails
      }

      const friendsData = await Promise.all(
        userFriendships.map(async (friendId) => {
          const friendDoc = await getDoc(doc(db, 'users', friendId));
          if (friendDoc.exists()) {
            return { userId: friendId, ...friendDoc.data() };
          }
          return null;
        })
      );

      setContacts(contactsData.filter(c => c));
      setFriends(friendsData.filter(f => f));
    } catch (error) {
      console.error('Error loading contacts and friends:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (contact) => {
    if (!eventId || !userId || inviting[contact.id]) return;

    setInviting({ ...inviting, [contact.id]: true });
    try {
      // Get target user ID (contact might be a friend or contact)
      const targetUserId = contact.userId || contact.userData?.userId || (contact.userData && contact.id);
      
      if (!targetUserId) {
        // If no userId, just generate link (for non-users)
        const token = await generateInviteToken(
          { kind: 'event_join', id: eventId },
          userId,
          { oneTime: false, expiresIn: 7 * 24 * 60 * 60 } // 7 days
        );
        const inviteURL = generateInviteURL(token);
        try {
          await navigator.clipboard.writeText(inviteURL);
          alert(`✅ Invite link copied to clipboard!\n\nShare this link with ${contact.name || contact.userData?.firstName || 'them'}`);
        } catch (err) {
          prompt('Copy this invite link:', inviteURL);
        }
        setInviting({ ...inviting, [contact.id]: false });
        return;
      }

      // Generate invite token
      const token = await generateInviteToken(
        { kind: 'event_join', id: eventId },
        userId,
        { oneTime: false, expiresIn: 7 * 24 * 60 * 60 }, // 7 days
        { role: 'member' }
      );

      // Get inviter name
      const inviterName = currentUser?.firstName && currentUser?.lastName
        ? `${currentUser.firstName} ${currentUser.lastName}`.trim()
        : currentUser?.firstName || currentUser?.email || 'Someone';

      // Create notification for the invited user
      await createEventInviteNotification(
        targetUserId,
        eventId,
        eventTitle || 'Event',
        inviterName,
        token,
        userId
      );

      const inviteURL = generateInviteURL(token);

      // Try to send SMS if phone number is available
      let smsSent = false;
      let phoneNumber = null;
      
      // Get phone number from contact or user data
      if (contact.phoneE164) {
        phoneNumber = contact.phoneE164;
      } else if (contact.userData?.phone) {
        phoneNumber = contact.userData.phone;
      } else if (targetUserId) {
        // Try to get phone from user document
        try {
          const userDoc = await getDoc(doc(db, 'users', targetUserId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            phoneNumber = userData.phone || userData.phoneE164;
          }
        } catch (phoneError) {
          console.warn('Could not fetch user phone number:', phoneError);
        }
      }

      if (phoneNumber) {
        try {
          await sendEventInviteSMS(
            phoneNumber,
            inviteURL,
            eventTitle || 'Event',
            inviterName,
            targetUserId, // recipientUserId for consent check
            userId // senderUserId for friends_only check
          );
          smsSent = true;
        } catch (smsError) {
          console.warn('Failed to send SMS invite (notification was still sent):', smsError);
          // Don't fail the invite if SMS fails (user may have opted out)
        }
      }

      // Copy to clipboard
      try {
        await navigator.clipboard.writeText(inviteURL);
        const smsStatus = smsSent ? ' SMS sent and' : '';
        alert(`✅ Invite sent to ${contact.name || contact.userData?.firstName || 'them'}!${smsStatus} Notification created.\n\nInvite link also copied to clipboard.`);
      } catch (err) {
        // Fallback: show link
        const smsStatus = smsSent ? ' SMS sent and' : '';
        alert(`✅ Invite sent to ${contact.name || contact.userData?.firstName || 'them'}!${smsStatus} Notification created.`);
        prompt('Copy this invite link:', inviteURL);
      }
    } catch (error) {
      console.error('Error creating invite:', error);
      alert('Failed to create invite. Please try again.');
    } finally {
      setInviting({ ...inviting, [contact.id]: false });
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const search = searchTerm.toLowerCase();
    const name = (contact.name || contact.userData?.firstName || '').toLowerCase();
    const phone = (contact.phoneE164 || '').toLowerCase();
    const email = (contact.email || contact.userData?.email || '').toLowerCase();
    return name.includes(search) || phone.includes(search) || email.includes(search);
  });

  const filteredFriends = friends.filter(friend => {
    const search = searchTerm.toLowerCase();
    const name = `${friend.firstName || ''} ${friend.lastName || ''}`.toLowerCase();
    const email = (friend.email || '').toLowerCase();
    return name.includes(search) || email.includes(search);
  });

  if (!isOpen) return null;

  return (
    <div className="invite-modal-overlay" onClick={onClose}>
      <div className="invite-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="invite-modal-header">
          <h2>Invite to {eventTitle || 'Event'}</h2>
          <button className="invite-modal-close" onClick={onClose}>×</button>
        </div>

        {/* Universal Invite QR Code/Link Button */}
        {eventId && (
          <div className="invite-universal-section">
            <button
              className="btn-universal-invite"
              onClick={() => setShowQRModal(true)}
              title="Generate a QR code or link that anyone can use to join"
            >
              📱 Universal Invite QR Code / Link
            </button>
            <small className="invite-universal-hint">
              {event?.visibility === 'public' 
                ? 'Anyone can scan this QR code or use this link to join the event'
                : event?.rules?.membersCanInvite
                ? 'This invite respects event restrictions (sponsor system, etc.)'
                : 'This invite link allows people to request to join the event'}
            </small>
          </div>
        )}

        <div className="invite-modal-search">
          <input
            type="text"
            placeholder="Search contacts or friends..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="invite-search-input"
          />
        </div>

        {/* Tabs */}
        <div className="invite-modal-tabs">
          <button
            className={`invite-tab ${activeTab === 'contacts' ? 'active' : ''}`}
            onClick={() => setActiveTab('contacts')}
          >
            Contacts ({contacts.length})
          </button>
          <button
            className={`invite-tab ${activeTab === 'friends' ? 'active' : ''}`}
            onClick={() => setActiveTab('friends')}
          >
            Friends ({friends.length})
          </button>
        </div>

        {loading ? (
          <div className="invite-modal-loading">Loading contacts...</div>
        ) : (
          <div className="invite-modal-body">
            {activeTab === 'contacts' ? (
              filteredContacts.length > 0 ? (
                <div className="invite-list">
                  {filteredContacts.map((contact) => (
                    <div key={contact.id} className="invite-item">
                      <div className="invite-item-info">
                        <div className="invite-item-name">
                          {contact.name || contact.userData?.firstName || 'Unknown'}
                          {contact.userData && (
                            <span className="invite-item-badge">Hotelia User</span>
                          )}
                        </div>
                        <div className="invite-item-details">
                          {contact.phoneE164 && <span>📱 {contact.phoneE164}</span>}
                          {contact.email && <span>✉️ {contact.email}</span>}
                        </div>
                      </div>
                      <button
                        className="btn-invite"
                        onClick={() => handleInvite(contact)}
                        disabled={inviting[contact.id]}
                      >
                        {inviting[contact.id] ? 'Inviting...' : 'Invite'}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="invite-empty">
                  <p>No contacts found{searchTerm && ` matching "${searchTerm}"`}.</p>
                  <small>Add contacts in the Contacts page to invite them to events.</small>
                </div>
              )
            ) : (
              filteredFriends.length > 0 ? (
                <div className="invite-list">
                  {filteredFriends.map((friend) => (
                    <div key={friend.userId} className="invite-item">
                      <div className="invite-item-info">
                        <div className="invite-item-name">
                          {`${friend.firstName || ''} ${friend.lastName || ''}`.trim() || 'Friend'}
                          <span className="invite-item-badge">Friend</span>
                        </div>
                        <div className="invite-item-details">
                          {friend.email && <span>✉️ {friend.email}</span>}
                        </div>
                      </div>
                      <button
                        className="btn-invite"
                        onClick={() => handleInvite({ id: friend.userId, userData: friend })}
                        disabled={inviting[friend.userId]}
                      >
                        {inviting[friend.userId] ? 'Inviting...' : 'Invite'}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="invite-empty">
                  <p>No friends found{searchTerm && ` matching "${searchTerm}"`}.</p>
                  <small>Add friends in the Contacts page to invite them to events.</small>
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Universal Invite QR Code Modal */}
      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        eventId={eventId}
        issuedBy={userId}
      />
    </div>
  );
};

export default InviteModal;

